import { loadableReady } from '@loadable/component';
import { type ComponentType, useEffect } from 'react';
import type { ErrorInfo } from 'react-dom/client';
import { hydrateRoot } from 'react-dom/client';
import type { RouteObject } from 'react-router';
import { RouterProvider, createBrowserRouter } from 'react-router';

import { AppConfigContextProvider } from '#/config/AppConfigContext';
import { SsrApiDataContextProvider } from '#/contexts/SsrApiDataContext';
import {
  UserEnvironmentContextProvider,
  useUserEnvironmentActions,
} from '#/contexts/UserEnvironmentContext';
import { I18nContext } from '#/i18n/contexts/I18nContext';
import { createI18nFn } from '#/i18n/createI18nFn';
import { useI18n } from '#/i18n/hooks/useI18n';
import { RenderingClientLocationHooks } from '#/rendering/client/components/RenderingClientLocationHooks';
import { renderingDeserializeHypernovaPayload } from '#/rendering/hypernova/client/renderingDeserializeHypernovaPayload';
import { setSsrApiData } from '#/rendering/state/ssrApiData';
import { routeObjectsWithRootErrorBoundary } from '#/routing/utils/routeObjectsWithRootErrorBoundary';
import { log } from '#/utils/logging';

import { configureUserTracking } from './utils/configureUserTracking';
import { renderingBootstrapClient } from './utils/renderingBootstrapClient';

type Args = {
  Component: ComponentType<Record<never, never>>;
  routeObjects: RouteObject[];
};

function LoadUserEnvironmentEffect() {
  const { setUserEnvironment } = useUserEnvironmentActions();
  const { locale } = useI18n();

  useEffect(() => {
    const { user, environment } = window.Idealist.initUserEnvironment;
    setUserEnvironment({ user, environment });

    configureUserTracking({ user, environment, locale });
  }, [locale, setUserEnvironment]);

  return null;
}

export async function renderSsrClient({ Component, routeObjects }: Args) {
  const payload = renderingDeserializeHypernovaPayload();
  if (!payload) return;

  const {
    node,
    data: { config, ssrApiData },
  } = payload;

  const { locale } = config.idealist;

  setSsrApiData(ssrApiData);

  // Bootstrap analytics & miscellaneous
  renderingBootstrapClient(config);

  // Setup @loadable
  await loadableReady();

  const wrappedRoutes = [
    {
      element: (
        <AppConfigContextProvider appConfig={config}>
          <I18nContext value={{ locale, i18n: createI18nFn(locale) }}>
            <SsrApiDataContextProvider value={ssrApiData}>
              <UserEnvironmentContextProvider
                initialState={{
                  user: null,
                  environment: null,
                  actionsWhileLoggedOutProcessedAt: null,
                }}
              >
                <Component />
                <RenderingClientLocationHooks />
                <LoadUserEnvironmentEffect />
              </UserEnvironmentContextProvider>
            </SsrApiDataContextProvider>
          </I18nContext>
        </AppConfigContextProvider>
      ),
      children: routeObjectsWithRootErrorBoundary(routeObjects),
    },
  ];

  const router = createBrowserRouter(wrappedRoutes);

  // Hydrate
  hydrateRoot(node, <RouterProvider router={router} />, {
    onRecoverableError: (error: unknown, errorInfo: ErrorInfo) => {
      window.Idealist.hydrationErrors = window.Idealist.hydrationErrors ?? [];
      window.Idealist.hydrationErrors.push({
        error,
        errorInfo,
      });
      if (config.idealist.onHydrationError === 'log-info') {
        log.info(error);
        log.info(errorInfo);
      } else if (config.idealist.onHydrationError === 'log-error') {
        log.error(error);
      } else if (config.idealist.onHydrationError === 'throw') {
        throw error;
      }
    },
  });
}
